import type { EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";
import type {
	InspectionDashboardHeat,
	InspectionDashboardSection,
} from "../../../models/InspectionDashboard";
import { formatCount, formatPercent, rate } from "../../utils/dashboardFormat";
import { DASHBOARD_INK } from "../../utils/dashboardTokens";

export type RankedGroup = {
	Id: number;
	Name: string;
	Readings: number;
	ChecksRecorded: number;
	FailedChecks: number;
	Defects: number;
};

export const toRankedSections = (
	sections: InspectionDashboardSection[],
): RankedGroup[] =>
	sections.map((section) => ({
		...section,
		Id: section.SectionId,
		Name: section.SectionName,
	}));

export const toRankedHeats = (
	heats: InspectionDashboardHeat[],
): RankedGroup[] =>
	heats.map((heat) => ({ ...heat, Id: heat.HeatId, Name: heat.HeatName }));

type ChartEvent = {
	componentType?: string;
	dataIndex?: number;
	value?: unknown;
};

type Props = {
	groups: RankedGroup[];
	onSelect?: (group: RankedGroup) => void;
	canSelect?: (group: RankedGroup) => boolean;
};

export const WorstGroupsChart = ({
	groups,
	onSelect,
	canSelect = () => true,
}: Props) => {
	const ranked = groups.slice(0, 5);

	const indexOf = (event: ChartEvent) =>
		event.componentType === "yAxis"
			? ranked.findIndex((group) => group.Name === event.value)
			: (event.dataIndex ?? -1);

	const handleClick = (event: ChartEvent) => {
		const group = ranked[indexOf(event)];

		if (onSelect && group && canSelect(group)) onSelect(group);
	};

	const handleLabelOver = (
		event: ChartEvent,
		chart: { dispatchAction: (action: Record<string, unknown>) => void },
	) => {
		if (event.componentType !== "yAxis") return;

		const dataIndex = indexOf(event);
		if (dataIndex >= 0)
			chart.dispatchAction({ type: "showTip", seriesIndex: 0, dataIndex });
	};

	const handleLabelOut = (
		event: ChartEvent,
		chart: { dispatchAction: (action: Record<string, unknown>) => void },
	) => {
		if (event.componentType === "yAxis")
			chart.dispatchAction({ type: "hideTip" });
	};

	const option: EChartsOption = {
		grid: { top: 8, right: 72, bottom: 24, left: 8, containLabel: true },
		tooltip: {
			trigger: "item",
			appendTo: "body",
			borderColor: DASHBOARD_INK.GRID,
			formatter: (parameters: unknown) => {
				const entry = parameters as { dataIndex?: number };
				const group = ranked[entry.dataIndex ?? 0];
				if (!group) return "";

				return `<div style="min-width:200px">
					<div style="font-weight:600">${group.Name}</div>
					<div style="color:${DASHBOARD_INK.SECONDARY};font-variant-numeric:tabular-nums">
						${formatCount(group.FailedChecks)} failed of ${formatCount(group.ChecksRecorded)} checks recorded
					</div>
					<div style="color:${DASHBOARD_INK.MUTED};font-size:11px;font-variant-numeric:tabular-nums">
						${formatCount(group.Readings)} readings · ${formatCount(group.Defects)} defects
					</div>
					${
						onSelect
							? `<div style="color:${DASHBOARD_INK.MUTED};font-size:11px;margin-top:4px;font-style:italic">
								${canSelect(group) ? "Click to open its latest production event" : "No production event in the last 6 months"}
							</div>`
							: ""
					}
				</div>`;
			},
		},
		xAxis: {
			type: "value",
			min: 0,
			axisLine: { show: false },
			axisTick: { show: false },
			axisLabel: {
				color: DASHBOARD_INK.MUTED,
				fontSize: 11,
				formatter: (value: number) => `${value}%`,
			},
			splitLine: { lineStyle: { color: DASHBOARD_INK.GRID } },
		},
		yAxis: {
			type: "category",
			inverse: true,
			data: ranked.map((group) => group.Name),
			triggerEvent: true,
			axisLine: { show: false },
			axisTick: { show: false },
			axisLabel: {
				color: DASHBOARD_INK.SECONDARY,
				fontSize: 12,
				fontWeight: 500,
			},
		},
		series: [
			{
				type: "bar",
				barMaxWidth: 14,
				cursor: onSelect ? "pointer" : "default",
				itemStyle: { borderRadius: [0, 4, 4, 0], color: "#2a78d6" },
				data: ranked.map(
					(group) => rate(group.FailedChecks, group.ChecksRecorded) ?? 0,
				),
				label: {
					show: true,
					position: "right",
					color: DASHBOARD_INK.SECONDARY,
					fontSize: 11,
					formatter: (parameters: unknown) => {
						const entry = parameters as { dataIndex?: number; value?: number };
						const group = ranked[entry.dataIndex ?? 0];

						return `${formatPercent(entry.value ?? null)} · ${formatCount(group?.FailedChecks ?? 0)}`;
					},
				},
			},
		],
	};

	return (
		<ReactECharts
			option={option}
			notMerge
			onEvents={{
				mouseover: handleLabelOver,
				mouseout: handleLabelOut,
				...(onSelect ? { click: handleClick } : {}),
			}}
			style={{ height: Math.max(180, ranked.length * 32 + 30), width: "100%" }}
			opts={{ renderer: "svg" }}
		/>
	);
};
